import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { AlertCircle, Plus, Loader2, CheckCircle2, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_BADGE = {
  new: { label: "Nouveau", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  in_progress: { label: "En cours", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  resolved: { label: "Résolue", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejetée", color: "bg-red-100 text-red-800", icon: X },
};

const TYPE_LABELS = { financial: "Financière", pedagogical: "Pédagogique", administrative: "Administrative", disciplinary: "Disciplinaire" };

export default function ComplaintsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type: "pedagogical", urgency: "medium", subject: "", description: "" });
  const [response, setResponse] = useState({});

  const load = () => api.get("/complaints").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject || !form.description) return toast.error("Veuillez remplir tous les champs");
    setLoading(true);
    try {
      await api.post("/complaints", form);
      toast.success("Réclamation envoyée. Notre équipe vous répondra rapidement.");
      setOpen(false);
      setForm({ type: "pedagogical", urgency: "medium", subject: "", description: "" });
      load();
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/complaints/${id}`, { status, response: response[id] || "" });
      toast.success("Réclamation mise à jour");
      setResponse({...response, [id]: ""});
      load();
    } catch { toast.error("Erreur"); }
  };

  const canManage = ["admin", "super_admin", "founder"].includes(user?.role);

  return (
    <div className="space-y-5" data-testid="page-complaints">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Réclamations</h2>
        {user?.role === "parent" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-complaint-btn"><Plus className="h-4 w-4" /> Nouvelle réclamation</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Déposer une réclamation</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type *</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                      <SelectTrigger data-testid="complaint-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Urgence *</Label>
                    <Select value={form.urgency} onValueChange={(v) => setForm({...form, urgency: v})}>
                      <SelectTrigger data-testid="complaint-urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Faible</SelectItem>
                        <SelectItem value="medium">Moyenne</SelectItem>
                        <SelectItem value="high">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Sujet *</Label><Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="complaint-subject" /></div>
                <div><Label>Description *</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="complaint-description" /></div>
                <Button onClick={submit} disabled={loading} className="bg-[#166534] text-white w-full" data-testid="complaint-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer la réclamation"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {items.map((c) => {
          const Status = STATUS_BADGE[c.status];
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`complaint-${c.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="chip">{TYPE_LABELS[c.type]}</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${Status.color}`}>
                      <Status.icon className="h-3 w-3" /> {Status.label}
                    </span>
                    {c.urgency === "high" && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">URGENT</span>}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#1E3A8A]">{c.subject}</h3>
                  <div className="text-xs text-slate-500 mt-1">Par {c.parent_name} · {fmtDate(c.created_at)}</div>
                </div>
              </div>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">{c.description}</p>
              {c.response && (
                <div className="mt-4 p-4 bg-green-50 border-l-4 border-[#166534] rounded">
                  <div className="text-xs font-bold text-[#166534] uppercase tracking-wider mb-1">Réponse de l&apos;administration</div>
                  <div className="text-sm text-slate-700">{c.response}</div>
                </div>
              )}
              {canManage && c.status !== "resolved" && c.status !== "rejected" && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Textarea placeholder="Votre réponse..." value={response[c.id] || ""} onChange={(e) => setResponse({...response, [c.id]: e.target.value})} rows={2} className="mb-2" data-testid={`complaint-reply-${c.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => updateStatus(c.id, "resolved")} className="bg-[#166534] text-white" data-testid={`resolve-${c.id}`}>Résoudre</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(c.id, "in_progress")} data-testid={`progress-${c.id}`}>En cours</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(c.id, "rejected")} className="border-red-300 text-red-700" data-testid={`reject-${c.id}`}>Rejeter</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-center text-slate-500 py-12 bg-white rounded-xl border border-slate-200">Aucune réclamation pour le moment</div>}
      </div>
    </div>
  );
}
